import React, { useState } from "react";

import { API } from "aws-amplify";

import { Backdrop, CircularProgress } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import {
  FormDataConsumer,
  SaveButton,
  useNotify,
  useRedirect,
} from "react-admin";
import { useForm } from "react-final-form";

import { sanitizeInbound } from "../../../graphql/inbound/inboundCRUD";
import {
  FilePaths,
  InboundState,
  InboundStatus,
  NoteType,
  WorkflowType,
} from "../../../utils/constants";
import { getCustomFieldsToSave } from "../../../components/common/CustomFields";
import { logError } from "../../../utils/appUtils";
import { saveRemoveFiles } from "../../../utils/fileUtils";
import {
  resolveScheduledDateTime,
  validateAppointment,
} from "../../../utils/dateUtils";
import { isBulkShipment } from "../../../utils/loadTypeUtils";
import { normalizeValue } from "../../../utils/utils";
import {
  calcNetWeight,
  lambdaSaveInbound,
} from "../../../managerApp/resources/inboundShipments/InboundUtils";
import { ConditionalConfirm } from "../../../components/common/ConditionalConfirm";

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
  },
}));

// Customer-portal twin of managerApp .../inboundShipments/advice/
// ValidateCreateAdviceButton. Builds an ADVICE_PROCESSING inbound scoped to the
// logged-in customer, records advisedBy for approve/reject notifications, then
// kicks off the /parseInboundAdvice AI pipeline (Bedrock/Textract).
export const ValidateCreateAdviceButton = ({
  account,
  appUser,
  selectedTimeslot,
  ...props
}) => {
  const classes = useStyles();
  const form = useForm();
  const notify = useNotify();
  const redirect = useRedirect();
  const [isSaving, setIsSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState("");

  const handleValidateSave = async () => {
    // Re-check timeslot availability just before saving (the slot may have been
    // taken while the form was open); mirrors the outbound create flow.
    const error = selectedTimeslot
      ? await validateAppointment(selectedTimeslot)
      : null;
    if (error) {
      setConfirmMessage(
        `${selectedTimeslot.time} is no longer available. Please select a new time.`,
      );
      setOpen(true);
    } else {
      handleSave();
    }
  };

  const handleDialogClose = () => setOpen(false);

  const handleSave = () => {
    setOpen(false);
    props.handleSubmitWithRedirect();
  };

  const transform = async (data) => {
    setIsSaving(true);

    try {
      const now = new Date().toISOString();

      data.placedDateTime = data.placedDateTime || null;
      data.notifyDateTime = data.notifyDateTime || null;
      data.scheduledDateTime = data.scheduledDateTime || null;
      data.createDateTime = now;
      data.releaseDateTime = data.releaseDateTime || null;

      const inbound = { ...data };

      /* ===============================
       * REQUIRED CORE FIELDS
       * =============================== */
      inbound.active = "true"; // String!
      inbound.status = InboundStatus.ADVICE_PROCESSING;
      inbound.state = InboundState.NEW;
      // Use the ACCOUNT's groups (not the logged-in customer user's), so the
      // record is readable by the yard/manager group too. InboundShipment auth
      // is groupsField-based: a user sees a record only if one of their Cognito
      // groups is in `groups`. A customer user's groups omit the yard group, so
      // stamping user groups here hides the advice from the manager portal.
      // Mirrors the working customer outbound create (outbounds/ValidateCreateButton).
      inbound.groups = account?.groups;
      inbound.reloadYardId = window.appUser?.reloadYardId;
      inbound.accountName = account.businessName;
      inbound.accountId = account.id;
      inbound.billToAccountId =
        inbound.billToAccountId || account.defaultBillToAccountId || account.id;

      // Recipient for the approve/reject notification emails.
      inbound.advisedBy = appUser?.email || window.appUser?.email;

      inbound.daysOnDemurrageClock = 0;

      /* ===============================
       * STRING FIELDS (never null)
       * =============================== */
      inbound.instructions = "";
      inbound.railCarNumber = inbound.railCarNumber || "";
      inbound.shipFrom = inbound.shipFrom || inbound.accountName;
      // GSI inboundShipmentByAccountByPoNumber: key attributes cannot be empty strings.
      const poTrimmed = (inbound.poNumber || "").trim();
      inbound.poNumber = poTrimmed || " ";
      inbound.normalizedPoNumber = normalizeValue(inbound.poNumber);

      inbound.lotType = account.lotType || inbound.lotType || "";
      inbound.lotNumberOverride = account?.lotNumberOverride;

      /* ===============================
       * OPTIONAL ID FIELDS → OMIT if empty
       * =============================== */
      if (!inbound.areaId) delete inbound.areaId;
      if (!inbound.zoneId) delete inbound.zoneId;
      if (!inbound.timeslotId) delete inbound.timeslotId;
      if (!inbound.carrierEquipmentTypeId)
        delete inbound.carrierEquipmentTypeId;

      /* ===============================
       * WEIGHTS
       * =============================== */
      inbound.scaleWeightUnits = inbound.weightUnits;

      const hasWeights = inbound.inventories?.items?.every(
        (i) => i.weight != null && !isNaN(i.weight),
      );

      if (hasWeights) {
        const weightUOM =
          inbound.weightUnits ||
          inbound.inventories.items[0]?.weightUnits ||
          inbound.inventories.items[0]?.weightUOM;
        inbound.netWeight = calcNetWeight(inbound, null, weightUOM);
        inbound.weightUnits = weightUOM;
      }

      /* ===============================
       * WORKFLOW TYPE
       * =============================== */
      inbound.workflowType = isBulkShipment(
        inbound.reloadYardCommodityId,
        inbound.areaId,
      )
        ? WorkflowType.BULK
        : WorkflowType.ACCEPT_STORE;

      /* ===============================
       * CUSTOM FIELDS
       * =============================== */
      inbound.customFields = getCustomFieldsToSave(
        inbound.customFields,
        account?.inboundCustomFields,
      );

      /* ===============================
       * HISTORY
       * =============================== */
      inbound.history = [
        {
          text: "Created Inbound Advice.",
          type: NoteType.GENERIC,
          date: now,
          by: window.appUser?.fullName,
        },
      ];

      if (!inbound.notes?.length) {
        delete inbound.notes;
      } else {
        inbound.notes.forEach((n) => {
          n.type = NoteType.GENERIC;
          n.date = now;
          n.by = window.appUser?.fullName;
        });
      }

      /* ===============================
       * FILES
       * =============================== */
      await saveRemoveFiles(
        undefined,
        inbound,
        "attachments",
        `${FilePaths.INBOUND}/${Date.now()}`,
      );

      /* ===============================
       * SANITIZE SIDE EFFECTS
       * =============================== */
      delete inbound.customFieldSelect;
      delete inbound.manifestFile;

      return sanitizeInbound(inbound);
    } catch (err) {
      logError(
        "inbounds:ValidateCreateAdviceButton:transform",
        err,
        notify,
      );
      return Promise.reject();
    }
  };

  const onSuccess = async ({ data }) => {
    // Book the picked timeslot before kicking off the AI pipeline. The record
    // was just created via plain AppSync (no timeslotId), so lambdaSaveInbound
    // with fullUpdate:false only creates the Timeslot appointment and links
    // timeslotId — it does not touch inventories/track cars (owned by the
    // parse pipeline). Booking first also avoids racing the pipeline's update.
    // Booking failures are non-fatal and handled in their own try/catch: the
    // advice already exists, and skipping the parse POST below would strand it
    // in ADVICE_PROCESSING forever with no edit path out.
    let timeslotFailed = false;
    if (selectedTimeslot) {
      // Resolved before the call so a yard with no time zone fails the booking
      // outright (with its own message) rather than sending an Invalid Date.
      const scheduledDateTime =
        data.scheduledDateTime ||
        resolveScheduledDateTime(selectedTimeslot, {
          notify,
          scope: "inbounds:ValidateCreateAdviceButton:bookTimeslot",
        });
      if (!scheduledDateTime) {
        timeslotFailed = true;
      } else {
        try {
          await lambdaSaveInbound(
            data,
            {
              ...data,
              timeslot: selectedTimeslot,
              scheduledDateTime,
            },
            false,
          );
        } catch (error) {
          timeslotFailed = true;
          logError(
            "inbounds:ValidateCreateAdviceButton:bookTimeslot",
            error,
            false,
          );
        }
      }
    }

    try {
      const response = await API.post("transREST", "/parseInboundAdvice", {
        body: {
          ...data,
          user: window.appUser.email,
          reloadYardId: window.appUser.reloadYardId,
        },
      });
      if (response.error) throw response.error;

      if (timeslotFailed)
        notify(
          "Inbound advice submitted, but the delivery appointment could not be scheduled. Please contact the yard.",
          "warning",
        );
      else notify("Inbound advice submitted successfully", "success");
      redirect("/inboundShipments");
    } catch (error) {
      logError(
        "inbounds:ValidateCreateAdviceButton:onSuccess",
        error,
        notify,
        redirect,
      );
    } finally {
      setIsSaving(false);
    }
  };

  const onFailure = (error) => {
    setIsSaving(false);
    const message = error?.errors?.[0]?.message || "";
    if (message?.startsWith("The variables input")) {
      const formState = form.getState();
      logError("inbounds:ValidateCreateAdviceButton:data", formState.values, false);
    }
    logError("inbounds:ValidateCreateAdviceButton:onFailure", error, notify);
  };

  // Pass ...props (not ...rest) to FormDataConsumer to include
  // handleSubmitWithRedirect from props.
  return (
    <FormDataConsumer>
      {() => (
        <>
          <Backdrop className={classes.backdrop} open={isSaving}>
            <CircularProgress color="inherit" />
          </Backdrop>
          <SaveButton
            id="save-button"
            label="Submit"
            submitOnEnter={false}
            undoable={false}
            saving={isSaving}
            handleSubmitWithRedirect={handleValidateSave}
            transform={transform}
            onSuccess={onSuccess}
            onFailure={onFailure}
          />
          <ConditionalConfirm
            isOpen={open}
            loading={isSaving}
            title="Save Confirmation"
            content={confirmMessage}
            cancel="Ok"
            onClose={handleDialogClose}
          />
        </>
      )}
    </FormDataConsumer>
  );
};
